
import { useStore } from '@/store'
import * as THREE from 'three'
import { defineComponent, inject, onUnmounted } from 'vue'
import { computed, watch } from 'vue'
import MaterialCreateService from '@/services/Three/material-create.service'
import References from '@/singletons/references'
import TextureLoadService from '@/services/Three/texture-load.service'
import nameSettings from '@/settings/name-settings.json'
import NameConverterService from '@/services/form/name-converter.service'
import BendModifier from '@/utils/BendModifier'
import Analytics from '@/singletons/analytics'
import SentrySingleton from '@/singletons/sentry-singleton'

export default defineComponent({
  name: 'ThreeRandselName',
  setup() {
    const store = useStore()
    const emitter: any = inject('emitter')
    const isSceneLoaded = computed(() => store.getters.isSceneLoaded)
    const configNamechar = computed(() => store.getters.configNamechar)
    const configNamethreadId = computed(() => store.getters.configNamethreadId)
    const isAnimationFinished = computed(() => store.getters.isAnimationFinished)
    const materialCreateService: MaterialCreateService = new MaterialCreateService()
    let textureLoadService: TextureLoadService
    let nameConverterService = new NameConverterService()
    let material: THREE.MeshPhongMaterial
    let font: THREE.Font
    let fontMesh: THREE.Mesh | null

    // prettier-ignore
    watch(() => isSceneLoaded.value, (_) => {
      try {
        const obj = nameSettings.nameitoColors.find((x: any) => x.default && x.default === true)
        const defaultColor = materialCreateService.createColor(obj!.value)

        material = new THREE.MeshPhongMaterial({
          color: defaultColor,
          flatShading: true,
          shininess: 0,
          specular: new THREE.Color(0x000000),
          emissive: defaultColor,
          emissiveIntensity: 0.1,
        });
      }
      catch(err) {
        SentrySingleton.instance.captureException(err)
      }
     
      const fontLoader = new THREE.FontLoader();
      fontLoader.load('fonts/GenYoMinH_SourceHanSansMedium.json', function(f) {
        font = f;
      }, function(xhr) {}, function(err) {
          console.log('error loading font: ', err);
          Analytics.instance.sendFontLoadErrorEvent("cannot load name font");
          SentrySingleton.instance.captureException(err)
      });

      try {
        initTextureLoadService()
        textureLoadService.loadImage('default/namestitch', (tex: THREE.Texture | null) => {
          tex!.wrapS = THREE.RepeatWrapping;
          tex!.wrapT = THREE.RepeatWrapping;
          tex!.repeat.set(75, 75);
          tex!.encoding = THREE.sRGBEncoding;
          material.map = tex
          material.needsUpdate = true
        })
      }
      catch(err) {
        SentrySingleton.instance.captureException(err)
      }
    })

    function initTextureLoadService() {
      if (!textureLoadService || textureLoadService == undefined) {
        textureLoadService = new TextureLoadService(References.instance.getSceneReferences().maxAnisotropy)
      }
    }

    // prettier-ignore
    watch(() => isAnimationFinished.value, (_) => { 
      createName(configNamechar.value)
    })

    // prettier-ignore
    watch(() => configNamechar.value, (name: string | null | undefined) => {
      if(!isAnimationFinished.value) {
        return
      }
      createName(name)
    })

    // prettier-ignore
    watch(() => configNamethreadId.value, (_) => {  updateColorOption() })
    // prettier-ignore
    emitter.on('updateConfig', onUpdateConfig)

    function onUpdateConfig() {
      updateColorOption()
      createName(configNamechar.value)
    }

    function updateColorOption() {
      try {
        if(configNamethreadId.value == '0') {
          console.log("configNamethreadId is '0'")
          return 
        }
        if (configNamethreadId.value) {
          setNameColor(nameSettings.nameitoColors.find((x: any) => x.id == configNamethreadId.value))
        } else {
          setNameColor(nameSettings.nameitoColors.find((x: any) => x.default && x.default === true))
        }
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    function createName(name: string | null | undefined) {
      try {
        removeMesh()
        if (!name || name.length <= 0 || !font) {
          return
        }
        var textGeo = new THREE.TextGeometry(nameConverterService.convertToFontMeshCharacters(name), {
          font: font,
          size: 0.0099,
          height: 0.0025,
          curveSegments: 8
        })
        fontMesh = new THREE.Mesh(textGeo, material)
        bendMesh(fontMesh, textGeo)
        fontMesh.position.set(0.0, 0.098, -0.114)
        fontMesh.rotation.set(0, Math.PI, 0)
        fontMesh.scale.set(1.03, 1, 1)
        References.instance.getSceneReferences().scene.add(fontMesh)
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    function bendMesh(fontMesh: THREE.Mesh, textGeo: THREE.TextGeometry) {
      var direction = new THREE.Vector3(0, 0, -1)
      var axis = new THREE.Vector3(0, -1, 0)
      var angle = Math.PI / 26
      BendModifier.set(direction, axis, angle)
      BendModifier.modify(fontMesh.geometry)

      textGeo.center()
      textGeo.computeBoundingBox()
      textGeo.computeVertexNormals()
    }

    function removeMesh() {
      if (fontMesh) {
        References.instance.getSceneReferences().scene.remove(fontMesh)
        fontMesh = null
      }
    }

    function setNameColor(obj: any) {
      if (!obj || !obj.value) {
        SentrySingleton.instance.captureMessage('unable to get name color')
        return
      }
      try {
        const color = materialCreateService.createColor(obj.value)
        material.color = color
        material.emissive = color
        material.needsUpdate = true
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    onUnmounted(() => {
      emitter.off('updateConfig', onUpdateConfig)
    })

    return {
      isAnimationFinished,
      isSceneLoaded,
      configNamechar,
      configNamethreadId
    }
  }
})
